import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import type { ScopedUidGroupsArgs, UidGroupRow } from '../canonicalPicker.types'
import { allRowsOf, BaseQuery } from './paging'
import {
  chunksOf,
  groupsOfProductsUrl,
  PRODUCTS_PER_REQUEST,
  scopedKeysUrls,
} from './query'
import { isUidGroupRow, pageOf } from './rows'

type Result = { data: UidGroupRow[] } | { error: FetchBaseQueryError }

const byNameThenProvider = (a: UidGroupRow, b: UidGroupRow): number =>
  a.name.localeCompare(b.name) ||
  a.provider.localeCompare(b.provider) ||
  a.uidGroup.localeCompare(b.uidGroup)

/**
 * Every UID group a level 2 offers under level 1's picks: the products its
 * smart source lists under them (the items carrying a modifier, or a
 * modifier's items), then the groups whose variants belong to any of them.
 * A product can sit under hundreds of others, more than one query can hold,
 * so the whole list is read and the dropdown pages it.
 */
const scopedUidGroups = async (
  { source, scopeSource, scope }: ScopedUidGroupsArgs,
  baseQuery: BaseQuery
): Promise<Result> => {
  const keys = await allRowsOf(scopedKeysUrls(scopeSource, scope), baseQuery)
  if ('error' in keys) return keys
  const productIds = Array.from(
    new Set(
      pageOf(keys.data, scopeSource, 'product', false).rows.map(row => row.id)
    )
  )

  const groups = await allRowsOf(
    chunksOf(productIds, PRODUCTS_PER_REQUEST).map(
      chunk => page => groupsOfProductsUrl(source, chunk, page)
    ),
    baseQuery
  )
  if ('error' in groups) return groups
  return {
    data: pageOf(groups.data, source, 'uidGroup', false)
      .rows.filter(isUidGroupRow)
      .sort(byNameThenProvider),
  }
}

export default scopedUidGroups
